export interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

export interface BlogCategory {
  id: number
  name: string
  slug: string
  description?: string
  icon_key?: string
  type?: 'menu' | 'route' | 'page'
  path?: string
  parent_id?: number | null
  sort_order?: number
}

export interface BlogTag {
  id: number
  name: string
  slug: string
}

export interface BlogLink {
  id: number
  name: string
  url: string
  logo?: string
  description?: string
  sort_order: number
}
export interface BlogNavigation { id: number; category_id: number; title: string; name: string; url: string; logo_url?: string; description?: string }
export interface BlogNavigationCategory { id: number; name: string; description?: string; icon_key?: string; sort_order?: number; navigations: BlogNavigation[] }
export interface BlogKnowledge { id: number; title: string; description?: string; cover_url?: string; is_public?: boolean; view_count?: number; created_at?: string; updated_at?: string }
export interface BlogNewsArticle { id: number; title: string; description?: string; content?: string; url: string; image_url?: string; source_name?: string; author?: string; language?: string; published_at?: string; fetched_at?: string }
export interface NewsPage { list: BlogNewsArticle[]; total: number; offset: number; next_offset: number; page_size: number; has_next: boolean }

export interface BlogAd {
  id: number
  title: string
  image_id?: number | null
  image_signed_url?: string
  link_url?: string
  description?: string
  position: string
  sort_order: number
}

export interface BlogPost {
  id: number
  title: string
  slug: string
  excerpt: string
  content?: string

  html_content?: string
  table_of_contents?: Array<{ id: string; title: string; level: number }>
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  published_at?: string
  created_at: string
  view_count: number
  like_count: number
  comment_count: number
  is_pinned: boolean
  article_level: number
  content_restricted?: boolean
  content_restriction_reason?: 'login' | 'upgrade'
  category?: BlogCategory

  author?: {
    id: number
    display_name?: string
    username?: string
    avatar_url?: string
  }
  tags?: BlogTag[]
  post_bg_signed_url?: string
  previous_post?: { id: number; title: string; slug: string }
  next_post?: { id: number; title: string; slug: string }
}

export interface PostPage {
  list: BlogPost[]
  total: number
  offset: number
  next_offset: number
  page_size: number
  has_next: boolean
}

export interface BlogMenuItem {
  type: string
  name: string
  slug?: string
  href: string
  icon_key?: string
  sort_order: number
  children?: BlogMenuItem[]
}

export interface BlogSidebarSection {
  key: string
  title: string
  items: BlogPost[]
  show_bg: boolean
  sort_order: number
  tags?: BlogTag[]
  links?: BlogLink[]
  stats?: {
    posts: number
    categories: number
    tags: number
  }
}

export interface ThemeConfigDoc {
  theme_config: { title: string; configs: Array<{ name: string; value: string; title: string }>; sub_configs?: any[] }
}

const emptyPostPage = (pageSize: number): PostPage => ({
  list: [],
  total: 0,
  offset: 0,
  next_offset: 0,
  page_size: pageSize,
  has_next: false
})

const normalizePostPage = (value: Partial<PostPage> | undefined, pageSize: number): PostPage => ({
  ...emptyPostPage(pageSize),
  ...value,
  list: Array.isArray(value?.list) ? value.list : []
})

export function useBlogApi() {
  const config = useRuntimeConfig()
  // Runtime config can be injected by Docker/Kubernetes as a non-string value.
  // Normalizing here prevents SSR from crashing with "src.replace is not a
  // function" before the public API fallback logic can run.
  const apiBase = String(config.public.apiBaseUrl || '/api/v1').replace(/\/$/, '')
  const apiOrigin = import.meta.server
    ? String(config.apiProxyTarget || 'http://127.0.0.1:8000').replace(/\/$/, '')
    : ''
  // SSR otherwise makes all page views appear as the Nuxt process itself to
  // the backend, defeating per-client anti-abuse limits for article reads.
  const clientHeaders = import.meta.server
    ? useRequestHeaders(['user-agent', 'x-forwarded-for', 'x-real-ip'])
    : undefined
  const { authorizationHeader, restore } = useMemberAuth()

  const requestURL = (path: string) => import.meta.server
    ? `${apiOrigin}${apiBase}${path}`
    : `${apiBase}${path}`

  const listPosts = async (offset = 0, pageSize = 6) => {
    const response = await $fetch<ApiResponse<PostPage>>(requestURL('/public/posts/page'), {
      query: { offset, page_size: pageSize },
      timeout: 5000
    })

    if (response.code !== 0) {
      throw new Error(response.message || '加载文章失败')
    }

    return normalizePostPage(response.data, pageSize)
  }

  const getPost = async (slug: string) => {
    const authorization = authorizationHeader()
    const response = await $fetch<ApiResponse<BlogPost>>(requestURL(`/public/posts/${encodeURIComponent(slug)}`), {
      headers: { ...clientHeaders, ...(authorization ? { Authorization: authorization } : {}) },
      timeout: 5000
    })
    if (response.code !== 0) throw new Error(response.message || '加载文章失败')
    return response.data
  }

  const getMenu = async () => {
    const response = await $fetch<ApiResponse<BlogMenuItem[]>>(requestURL('/public/menu'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载菜单失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getThemeConfig = async () => {
    const response = await $fetch<ApiResponse<ThemeConfigDoc>>(requestURL('/public/theme-config'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载页面配置失败')
    return response.data
  }

  const getNavigation = async () => {
    const response = await $fetch<ApiResponse<BlogNavigationCategory[]>>(requestURL('/public/navigation'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载导航失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getNews = async (offset = 0, pageSize = 10) => {
    const response = await $fetch<ApiResponse<NewsPage>>(requestURL('/public/news'), { query: { offset, page_size: pageSize }, timeout: 5000 })
    if (response.code !== 0) throw new Error(response.message || '加载新闻失败')
    return response.data || { list: [], total: 0, offset, next_offset: offset, page_size: pageSize, has_next: false }
  }

  const getKnowledges = async () => {
    const response = await $fetch<ApiResponse<BlogKnowledge[]>>(requestURL('/public/knowledges'), { timeout: 5000 })
    if (response.code !== 0) throw new Error(response.message || '加载知识库失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getKnowledge = async (id: string | number) => {
    restore()
    const authorization = authorizationHeader()
    const response = await $fetch<ApiResponse<{ knowledge: BlogKnowledge; nodes: Array<BlogKnowledge & { parent_id?: number | null; type: string; content?: string; html_content?: string; sort_order?: number; is_public?: boolean; level?: number; content_restricted?: boolean; content_restriction_reason?: 'login' | 'upgrade' }>; content_restricted?: boolean }>>(requestURL(`/public/knowledges/${id}`), { headers: { ...clientHeaders, ...(authorization ? { Authorization: authorization } : {}) }, timeout: 5000 })
    if (response.code !== 0) throw new Error(response.message || '加载知识库失败')
    return response.data
  }

  const getCategories = async () => {
    const response = await $fetch<ApiResponse<BlogCategory[]>>(requestURL('/public/categories'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载分类失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getTags = async () => {
    const response = await $fetch<ApiResponse<BlogTag[]>>(requestURL('/public/tags'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载标签失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getLinks = async () => {
    const response = await $fetch<ApiResponse<BlogLink[]>>(requestURL('/public/links'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载友情链接失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getAds = async () => {
    const response = await $fetch<ApiResponse<BlogAd[]>>(requestURL('/public/ads'), { timeout: 3000 })
    if (response.code !== 0) throw new Error(response.message || '加载广告失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const getSidebar = async (scope: 'home' | 'post' = 'home') => {
    const response = await $fetch<ApiResponse<BlogSidebarSection[]>>(requestURL('/public/sidebar'), {
      query: { scope },
      timeout: 3000
    })
    if (response.code !== 0) throw new Error(response.message || '加载侧栏失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const searchPosts = async (keyword: string) => {
    const response = await $fetch<ApiResponse<BlogPost[]>>(requestURL('/public/search'), {
      query: { q: keyword },
      timeout: 5000
    })
    if (response.code !== 0) throw new Error(response.message || '搜索文章失败')
    return Array.isArray(response.data) ? response.data : []
  }

  const listPostsByCategory = async (slug: string) => {
    const response = await $fetch<ApiResponse<BlogPost[]>>(requestURL(`/public/categories/${encodeURIComponent(slug)}/posts`), {
      timeout: 5000
    })
    if (response.code !== 0) throw new Error(response.message || '加载分类文章失败')
    return response.data
  }

  const listPostsByTag = async (slug: string) => {
    const response = await $fetch<ApiResponse<BlogPost[]>>(requestURL(`/public/tags/${encodeURIComponent(slug)}/posts`), {
      timeout: 5000
    })
    if (response.code !== 0) throw new Error(response.message || '加载标签文章失败')
    return response.data
  }

  return { listPosts, getPost, getMenu, getNavigation, getNews, getKnowledges, getKnowledge, getThemeConfig, getCategories, getTags, getLinks, getAds, getSidebar, searchPosts, listPostsByCategory, listPostsByTag }
}
